import logger from '../utils/logger';
/**
 * Sesli ve Görsel Bildirim Servisi
 * Modern web API'leri kullanarak sesli ve görsel bildirimler sağlar
 */

class NotificationService {
    constructor() {
        this.audioContext = null;
        this.audioBuffer = null;
        this.isAudioSupported = false;
        this.isNotificationSupported = false;
        this.notificationPermission = 'default';
        this.audioElement = null;

        this.init();
    }

    /**
     * Servisi başlat
     */
    async init() {
        try {
            // Web Audio API desteğini kontrol et (AudioContext oluşturma kullanıcı gesturuna ertelenecek)
            if (window.AudioContext || window.webkitAudioContext) {
                this.isAudioSupported = true;
                // Eagerly create AudioContext on first user interaction to satisfy autoplay policy
                const resumeOnGesture = () => {
                    if (!this.audioContext) {
                        this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
                        this.loadAudioFile();
                    } else if (this.audioContext.state === 'suspended') {
                        this.audioContext.resume().catch(() => {});
                    }
                    document.removeEventListener('click', resumeOnGesture);
                    document.removeEventListener('keydown', resumeOnGesture);
                };
                document.addEventListener('click', resumeOnGesture);
                document.addEventListener('keydown', resumeOnGesture);
            }

            // Web Notifications API desteğini kontrol et
            if ('Notification' in window) {
                this.isNotificationSupported = true;
                this.notificationPermission = Notification.permission;
            }

            // Ses dosyası AudioContext hazır olduğunda yüklenecek (resumeOnGesture içinde)

        } catch (error) {
            logger.error('❌ Bildirim servisi başlatılamadı:', error);
        }
    }

    /**
     * Ses dosyasını yükle
     */
    async loadAudioFile() {
        try {
            if (!this.isAudioSupported || !this.audioContext) return;

            // Ses dosyasını fetch ile yükle
            const response = await fetch('/sounds/notification.mp3');
            if (!response.ok) {
                console.warn('⚠️ Ses dosyası yüklenemedi, sistem sesi kullanılacak');
                return;
            }

            const arrayBuffer = await response.arrayBuffer();
            this.audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

        } catch (error) {
            console.warn('⚠️ Ses dosyası yüklenemedi, sistem sesi kullanılacak:', error);
        }
    }

    /**
     * Fallback ses elementi oluştur
     */
    createFallbackAudio() {
        try {
            this.audioElement = new Audio('/sounds/notification.mp3');
            this.audioElement.preload = 'auto';
        } catch (error) {
            console.warn('⚠️ Fallback ses elementi oluşturulamadı, sistem sesi kullanılacak:', error);
        }
    }

    /**
     * Bildirim izni iste
     */
    async requestPermission() {
        if (!this.isNotificationSupported) {
            console.warn('⚠️ Web Notifications API desteklenmiyor');
            return false;
        }

        try {
            if (this.notificationPermission === 'default') {
                const permission = await Notification.requestPermission();
                this.notificationPermission = permission;
                return permission === 'granted';
            }

            return this.notificationPermission === 'granted';
        } catch (error) {
            logger.error('❌ Bildirim izni alınamadı:', error);
            return false;
        }
    }

    /**
     * Sesli bildirim çal
     */
    async playSound() {
        try {
            // 🟢 Browser Autoplay Policy: Resume on user gesture if suspended
            if (this.audioContext && this.audioContext.state === 'suspended') {
                try {
                    await this.audioContext.resume();
                } catch (resErr) {
                    logger.error('❌ AudioContext resume error:', resErr);
                }
            } else if (this.audioContext && this.audioContext.state === 'closed') {
                console.warn('⚠️ AudioContext is closed, re-initializing...');
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            }

            // Web Audio API ile çal
            if (this.audioContext && this.audioBuffer) {
                const source = this.audioContext.createBufferSource();
                source.buffer = this.audioBuffer;
                source.connect(this.audioContext.destination);
                source.start(0);
                return;
            }

            // Fallback: HTML5 Audio ile çal
            if (this.audioElement) {
                this.audioElement.currentTime = 0;
                await this.audioElement.play();
                return;
            }

            // Son çare: Sistem sesi çal
            this.playSystemSound();

        } catch (error) {
            console.warn('⚠️ Sesli bildirim çalınamadı, sistem sesi kullanılıyor:', error);
            this.playSystemSound();
        }
    }

    /**
     * Sistem sesi çal (fallback)
     */
    playSystemSound() {
        try {
            if (!this.audioContext) {
                console.warn('⚠️ AudioContext mevcut değil, ses çalınamıyor');
                return;
            }

            // Modern bildirim sesi oluştur (ding-dong benzeri)
            const oscillator1 = this.audioContext.createOscillator();
            const oscillator2 = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();

            oscillator1.connect(gainNode);
            oscillator2.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            // İlk ses (ding)
            oscillator1.frequency.setValueAtTime(800, this.audioContext.currentTime);
            oscillator1.frequency.setValueAtTime(600, this.audioContext.currentTime + 0.15);

            // İkinci ses (dong) - 0.2 saniye sonra
            oscillator2.frequency.setValueAtTime(600, this.audioContext.currentTime + 0.2);
            oscillator2.frequency.setValueAtTime(400, this.audioContext.currentTime + 0.35);

            // Ses seviyesi kontrolü
            gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
            gainNode.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + 0.05);
            gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.15);

            // İkinci ses için tekrar
            gainNode.gain.setValueAtTime(0, this.audioContext.currentTime + 0.2);
            gainNode.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + 0.25);
            gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.35);

            oscillator1.start(this.audioContext.currentTime);
            oscillator1.stop(this.audioContext.currentTime + 0.15);

            oscillator2.start(this.audioContext.currentTime + 0.2);
            oscillator2.stop(this.audioContext.currentTime + 0.35);

        } catch (error) {
            console.warn('⚠️ Sistem sesi çalınamadı:', error);
        }
    }

    /**
     * Görsel bildirim göster
     */
    async showNotification(title, options = {}) {
        try {
            // Bildirim izni kontrol et
            const hasPermission = await this.requestPermission();
            if (!hasPermission) {
                console.warn('⚠️ Bildirim izni yok, sadece sesli bildirim çalınacak');
                await this.playSound();
                return;
            }

            // Varsayılan bildirim ayarları
            const defaultOptions = {
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: 'customer-notification',
                requireInteraction: false,
                silent: false,
                ...options
            };

            // Bildirim göster
            const notification = new Notification(title, defaultOptions);

            // Bildirim tıklama olayı
            notification.onclick = () => {
                window.focus();
                notification.close();

                if (options.onClick) {
                    options.onClick();
                }
            };

            // Bildirim kapanma olayı
            notification.onclose = () => {
                if (options.onClose) {
                    options.onClose();
                }
            };


        } catch (error) {
            logger.error('❌ Görsel bildirim gösterilemedi:', error);
            // Hata durumunda sadece sesli bildirim çal
            await this.playSound();
        }
    }

    /**
     * Müşteri bildirimi göster
     */
    async showCustomerNotification(customerName, customerCount) {
        const title = 'Yeni Müşteri Bekliyor!';
        const options = {
            body: `${customerName} adlı müşteri sıraya eklendi. Toplam ${customerCount} bekleyen müşteri var.`,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: 'customer-notification',
            requireInteraction: false,
            silent: true, // FE-85: playSound() zaten çalıyor — OS bildirim sesiyle çift çalmayı engelle
            vibrate: [200, 100, 200], // Titreşim deseni
            onClick: () => {
                // Bildirime tıklandığında sayfayı odakla
                window.focus();
            }
        };

        // Hem sesli hem görsel bildirim göster
        await Promise.all([
            this.playSound(),
            this.showNotification(title, options)
        ]);
    }

    /**
     * ✅ YENİ: Davet bildirimi göster
     */
    async showInviteNotification(sessionId, inviteCount) {
        const title = 'Yeni Davet Linki Oluşturuldu!';
        const options = {
            body: `${sessionId} oturumu için yeni davet linki oluşturuldu. Toplam ${inviteCount} aktif davet var.`,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: 'invite-notification',
            requireInteraction: false,
            silent: true, // FE-85: playSound() zaten çalıyor — OS bildirim sesiyle çift çalmayı engelle
            vibrate: [200, 100, 200], // Titreşim deseni
            onClick: () => {
                // Bildirime tıklandığında sayfayı odakla
                window.focus();

                // Admin panelinde davetler sekmesine yönlendir
                const event = new CustomEvent('navigate-to-invites');
                window.dispatchEvent(event);
            }
        };

        // Hem sesli hem görsel bildirim göster
        await Promise.all([
            this.playSound(),
            this.showNotification(title, options)
        ]);
    }

    /**
     * ✅ YENİ: Bağlantı isteği bildirimi göster
     */
    async showConnectionRequestNotification(agentName, agentDepartment) {
        const title = 'Müşteri Temsilcisi Bağlantı İsteği!';
        const options = {
            body: `${agentName} (${agentDepartment}) adlı temsilci sizinle görüşmek istiyor.`,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: 'connection-request',
            requireInteraction: false,
            silent: true, // FE-85: playSound() zaten çalıyor — OS bildirim sesiyle çift çalmayı engelle
            vibrate: [200, 100, 200], // Titreşim deseni
            onClick: () => {
                // Bildirime tıklandığında sayfayı odakla
                window.focus();
            }
        };

        // Hem sesli hem görsel bildirim göster
        await Promise.all([
            this.playSound(),
            this.showNotification(title, options)
        ]);
    }

    /**
     * Bildirim servisini temizle
     */
    cleanup() {
        try {
            if (this.audioContext) {
                this.audioContext.close();
            }
            if (this.audioElement) {
                this.audioElement.pause();
                this.audioElement.src = '';
            }
        } catch (error) {
            logger.error('❌ Bildirim servisi temizlenirken hata:', error);
        }
    }
}

// Singleton instance oluştur
const notificationService = new NotificationService();

export default notificationService; 
